<template>
  <div class="ask-audio" :class="{ 'is-expanded': audioUrl || loading, 'is-playing': playing, 'is-loading': loading }">
    <button
      class="ask-audio-toggle"
      type="button"
      :aria-label="buttonLabel"
      :aria-pressed="playing"
      @click="toggle"
      @pointerup="releasePointerFocus"
    >
      <span class="ask-audio-icon" aria-hidden="true">
        <span v-if="loading" class="ask-audio-spinner"></span>
        <svg v-else-if="playing" viewBox="0 0 16 16" fill="currentColor">
          <rect x="3.2" y="2.7" width="3.1" height="10.6" rx="1"></rect>
          <rect x="9.7" y="2.7" width="3.1" height="10.6" rx="1"></rect>
        </svg>
        <svg v-else viewBox="0 0 16 16" fill="currentColor">
          <path d="M4.2 2.8c0-.72.8-1.15 1.4-.75l8 5.2a.9.9 0 0 1 0 1.5l-8 5.2a.9.9 0 0 1-1.4-.75V2.8Z"></path>
        </svg>
      </span>
      <span class="ask-audio-copy">
        <span class="ask-audio-label">{{ playbackLabel }}</span>
        <span class="ask-audio-byline">Henry’s AI voice</span>
      </span>
    </button>

    <div class="ask-audio-progress" :aria-hidden="duration <= 0">
      <input
        v-if="duration > 0"
        class="ask-audio-seek"
        type="range"
        min="0"
        :max="duration"
        step="0.1"
        :value="currentTime"
        :style="{ '--ask-audio-progress': `${progressPercent}%` }"
        aria-label="Answer playback position"
        :aria-valuetext="`${clock(currentTime)} of ${clock(duration)}`"
        @input.stop="seek"
      >
      <span v-else class="ask-audio-rail"><span :class="{ 'is-active': loading }"></span></span>
    </div>
    <span v-if="duration > 0" class="ask-audio-time">{{ clock(currentTime) }} / {{ clock(duration) }}</span>
    <p v-if="error" class="ask-audio-error" role="status">{{ error }}</p>
    <span class="ask-audio-status" role="status">{{ loading ? 'Preparing voice…' : '' }}</span>
    <audio
      ref="audio"
      preload="metadata"
      crossorigin="anonymous"
      @loadedmetadata="updateTime"
      @durationchange="updateTime"
      @timeupdate="updateTime"
      @seeked="updateTime"
      @play="handlePlay"
      @playing="loading = false"
      @waiting="loading = playing"
      @pause="handlePause"
      @ended="handleEnded"
      @error="playbackError"
    />
  </div>
</template>

<script>
import { setSpeechPlaybackRate } from '../../utils/speechPlayback';
import { requestAskAudio } from '../../utils/askAudio';
import { createStreamingAudio } from '../../utils/streamingAudio';

export default {
  name: 'AskAnswerAudio',
  props: { text: { type: String, required: true } },
  data: () => ({ audioUrl: '', loading: false, playing: false, currentTime: 0, duration: 0, error: '' }),
  computed: {
    playbackLabel() {
      if (this.loading) return 'Preparing voice';
      if (this.playing) return 'Listening';
      if (this.duration > 0 && this.currentTime >= this.duration) return 'Listen again';
      if (this.currentTime > 0) return 'Continue listening';
      return 'Listen to this answer';
    },
    buttonLabel() {
      if (this.loading) return 'Cancel preparing answer audio';
      return `${this.playing ? 'Pause' : 'Play'} this answer in Henry’s AI voice`;
    },
    progressPercent() {
      return this.duration > 0 ? Math.min(100, Math.max(0, (this.currentTime / this.duration) * 100)) : 0;
    },
  },
  watch: { text() { this.reset(); } },
  mounted() {
    this._onOtherAudio = (event) => {
      if (event.detail === this) return;
      if (this.loading) this.reset();
      else this.$refs.audio?.pause();
    };
    window.addEventListener('view-summary-play', this._onOtherAudio);
  },
  beforeUnmount() {
    window.removeEventListener('view-summary-play', this._onOtherAudio);
    this.reset();
  },
  methods: {
    reset() {
      this._playbackId = (this._playbackId || 0) + 1;
      this._request?.abort();
      this._request = null;
      this.$refs.audio?.pause();
      this._stream?.dispose();
      this._stream = null;
      this.$refs.audio?.removeAttribute('src');
      this.$refs.audio?.load();
      this.audioUrl = '';
      this.loading = false;
      this.playing = false;
      this.currentTime = 0;
      this.duration = 0;
      this.error = '';
    },
    async toggle() {
      if (this.loading) { this.reset(); return; }
      if (this.playing) { this.$refs.audio?.pause(); return; }
      const playbackId = this._playbackId = (this._playbackId || 0) + 1;
      this.error = '';
      if (this.audioUrl) { await this.startPlayback(playbackId); return; }
      const controller = new AbortController();
      this._request = controller;
      this.loading = true;
      const timeout = setTimeout(() => controller.abort(), 120000);
      try {
        const stream = this._stream = createStreamingAudio(this.$refs.audio, controller.signal);
        this.audioUrl = stream.url;
        // Start the media element in the click gesture, before waiting on Fish.
        if (stream.url) this.startPlayback(playbackId);
        const response = await requestAskAudio(this.text, controller.signal);
        if (this._request !== controller) { await response.body?.cancel(); return; }
        await stream.load(response);
        if (this._request !== controller) return;
        if (!this.audioUrl) {
          this.audioUrl = stream.url;
          await this.startPlayback(playbackId);
        }
        this.updateTime();
      } catch {
        if (this._request === controller) {
          this.reset();
          this.error = 'Audio is temporarily unavailable. Try again.';
        }
      } finally {
        clearTimeout(timeout);
        if (this._request === controller) this._request = null;
      }
    },
    async startPlayback(playbackId) {
      const audio = this.$refs.audio;
      if (!audio || playbackId !== this._playbackId) return;
      if (audio.ended) audio.currentTime = 0;
      setSpeechPlaybackRate(audio);
      try { await audio.play(); }
      catch {
        if (playbackId === this._playbackId && this.audioUrl) {
          this.loading = false;
          this.error = 'Your audio is ready. Tap play to listen.';
        }
      }
    },
    handlePlay() {
      this.playing = true;
      window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
    },
    handlePause() {
      this.loading = false; this.playing = false;
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    updateTime() {
      const audio = this.$refs.audio;
      if (Number.isFinite(audio?.duration)) this.duration = audio.duration;
      this.currentTime = audio?.currentTime || 0;
    },
    seek(event) {
      const audio = this.$refs.audio;
      const time = Number(event.target.value);
      if (!audio || !this.duration || !Number.isFinite(time)) return;
      audio.currentTime = Math.min(this.duration, Math.max(0, time));
      this.updateTime();
    },
    handleEnded() {
      this.playing = false;
      this.loading = false;
      this.updateTime();
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    playbackError() {
      if (!this.audioUrl) return;
      this.reset();
      this.error = 'Audio could not be played. Try again.';
    },
    releasePointerFocus(event) { event.currentTarget?.blur(); },
    clock(seconds) {
      const value = Math.floor(seconds || 0);
      return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.ask-audio {
  --ask-audio-track: color-mix(in srgb, currentColor 13%, transparent);
  --ask-audio-fill: color-mix(in srgb, currentColor 62%, transparent);
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  width: min(100%, 30rem);
  min-height: 44px;
  margin: .82rem 0 .5rem;
  color: inherit;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif;
}
.ask-audio-toggle {
  display: inline-flex;
  grid-column: 1;
  align-items: center;
  gap: .62rem;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.ask-audio-icon {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: color-mix(in srgb, currentColor 6%, transparent);
  transition: transform 110ms ease-out, background-color 160ms ease, box-shadow 160ms ease;
}
.ask-audio-icon svg { width: 13px; height: 13px; }
.ask-audio-toggle:hover .ask-audio-icon,
.ask-audio-toggle:focus-visible .ask-audio-icon,
.is-playing .ask-audio-icon,
.is-loading .ask-audio-icon { background: color-mix(in srgb, currentColor 10%, transparent); }
.ask-audio-toggle:active .ask-audio-icon { transform: scale(.94); }
.ask-audio-toggle:focus-visible { outline: none; }
.ask-audio-toggle:focus-visible .ask-audio-icon { box-shadow: 0 0 0 3px rgba(var(--site-accent-rgb), .22); }
.ask-audio-copy { display: flex; flex-direction: column; gap: .08rem; white-space: nowrap; }
.ask-audio-label { font-size: .76rem; font-weight: 590; line-height: 1.25; letter-spacing: .002em; }
.ask-audio-byline { color: var(--site-text-tertiary); font-size: .64rem; line-height: 1.25; }
.ask-audio-progress {
  grid-column: 2;
  width: 0;
  min-width: 0;
  margin-left: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateX(-4px);
  transition: width 280ms cubic-bezier(.25, .8, .25, 1), margin 280ms cubic-bezier(.25, .8, .25, 1), opacity 160ms ease, transform 280ms cubic-bezier(.25, .8, .25, 1);
}
.is-expanded .ask-audio-progress { width: calc(100% - 1rem); margin-left: 1rem; opacity: 1; transform: translateX(0); }
.ask-audio-time {
  grid-column: 3;
  margin-left: .7rem;
  color: var(--site-text-tertiary);
  font-size: .62rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ask-audio-seek {
  --ask-audio-progress: 0%;
  display: block;
  width: 100%;
  height: 18px;
  margin: 0;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.ask-audio-seek::-webkit-slider-runnable-track {
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(to right, var(--ask-audio-fill) var(--ask-audio-progress), var(--ask-audio-track) var(--ask-audio-progress));
}
.ask-audio-seek::-moz-range-track { height: 2px; border: 0; border-radius: 999px; background: var(--ask-audio-track); }
.ask-audio-seek::-moz-range-progress { height: 2px; border-radius: 999px; background: var(--ask-audio-fill); }
.ask-audio-seek::-webkit-slider-thumb {
  width: 9px;
  height: 9px;
  margin-top: -3.5px;
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  border-radius: 50%;
  background: currentColor;
  opacity: 0;
  transition: opacity 120ms ease;
}
.ask-audio-seek::-moz-range-thumb { width: 9px; height: 9px; border: 0; border-radius: 50%; background: currentColor; opacity: 0; transition: opacity 120ms ease; }
.ask-audio-seek:hover::-webkit-slider-thumb,
.ask-audio-seek:focus-visible::-webkit-slider-thumb,
.ask-audio-seek:active::-webkit-slider-thumb { opacity: 1; }
.ask-audio-seek:hover::-moz-range-thumb,
.ask-audio-seek:focus-visible::-moz-range-thumb,
.ask-audio-seek:active::-moz-range-thumb { opacity: 1; }
.ask-audio-rail { display: block; height: 2px; overflow: hidden; border-radius: 999px; background: var(--ask-audio-track); }
.ask-audio-rail span { display: block; width: 22%; height: 100%; border-radius: inherit; background: var(--ask-audio-fill); opacity: 0; }
.ask-audio-rail span.is-active { opacity: .7; animation: ask-audio-prepare 1.35s ease-in-out infinite; }
.ask-audio-error { grid-column: 1 / -1; margin: .15rem 0 0 2.62rem; color: var(--site-text-tertiary); font-size: .68rem; line-height: 1.35; }
.ask-audio-spinner { width: 11px; height: 11px; border: 1.4px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: ask-audio-spin 800ms linear infinite; }
.ask-audio-status { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
@keyframes ask-audio-spin { to { transform: rotate(360deg); } }
@keyframes ask-audio-prepare { from { transform: translateX(-110%); } to { transform: translateX(460%); } }
@media (max-width: 620px) {
  .ask-audio { width: 100%; }
  .ask-audio-label { font-size: .78rem; }
}
@media (prefers-contrast: more) {
  .ask-audio { --ask-audio-track: color-mix(in srgb, currentColor 28%, transparent); --ask-audio-fill: currentColor; }
}
@media (prefers-reduced-motion: reduce) {
  .ask-audio-icon,
  .ask-audio-progress,
  .ask-audio-seek::-webkit-slider-thumb,
  .ask-audio-seek::-moz-range-thumb { transition: none; }
  .ask-audio-spinner,
  .ask-audio-rail span.is-active { animation: none; }
}
</style>
